import { usePermissions } from '../contexts/PermissionContext';
import type { Role } from '../pages/users/types/rbac';
import { USER_ROLE_STORAGE_KEY } from './userProfileRouteAccess';

/** Display roles for shell nav, settings, and route guards (must stay aligned with `App.tsx`). */
export type DisplayUserRole = 'Unify Admin' | 'Client' | 'Provider' | 'Practice Owner' | 'Practice Admin';

const DISPLAY_USER_ROLE_VALUES: DisplayUserRole[] = [
  'Unify Admin',
  'Client',
  'Provider',
  'Practice Owner',
  'Practice Admin',
];

/**
 * Maps Medplum `Role` slugs → display shells (nav chrome).
 *
 * - `unify-assistant` shares the Unify Admin nav shell (same layout, fewer permissions).
 * - `supervisor` and `practice-assistant` share Provider and Practice Admin shells respectively.
 * - Switcher labels use `ROLE_LABELS` (slug), not this collapsed shell name.
 */
export const ROLE_SLUG_TO_DISPLAY_USER_ROLE: Record<string, DisplayUserRole> = {
  'unify-admin':        'Unify Admin',
  'unify-assistant':    'Unify Admin',
  'practice-owner':     'Practice Owner',
  'practice-admin':     'Practice Admin',
  'practice-assistant': 'Practice Admin',
  supervisor:           'Provider',
  provider:             'Provider',
  client:               'Client',
};

export function activeRoleStorageKey(practitionerId: string): string {
  return `unify.activeRole.${practitionerId}`;
}

export function getEffectiveActiveRoleSlug(
  availableRoles: Role[],
  storedSlug: string | undefined,
  primaryRole: Role,
): Role {
  const coercedStored = storedSlug
    ? coerceSwitchableRoleSlug(storedSlug as Role, availableRoles)
    : undefined;
  if (coercedStored && availableRoles.includes(coercedStored)) {
    return coercedStored;
  }
  const coercedPrimary = coerceSwitchableRoleSlug(primaryRole, availableRoles);
  if (availableRoles.includes(coercedPrimary)) return coercedPrimary;
  return availableRoles[0] ?? primaryRole;
}

function matchingSlugForDisplayShell(
  availableRoles: Role[],
  shell: DisplayUserRole,
  primaryRole: Role,
): Role | undefined {
  const matching = availableRoles.filter((slug) => ROLE_SLUG_TO_DISPLAY_USER_ROLE[slug] === shell);
  if (matching.includes(primaryRole)) return primaryRole;
  return matching[0];
}

/** Read session active role slug; migrate legacy `unify.userRole` DisplayUserRole key once. */
export function readStoredActiveRoleSlug(
  practitionerId: string | undefined,
  availableRoles: Role[],
  primaryRole: Role,
): Role {
  if (typeof window === 'undefined' || !practitionerId) {
    return getEffectiveActiveRoleSlug(availableRoles, undefined, primaryRole);
  }
  const key = activeRoleStorageKey(practitionerId);
  const stored = window.localStorage.getItem(key);
  if (stored && availableRoles.includes(stored as Role)) {
    return getEffectiveActiveRoleSlug(availableRoles, stored, primaryRole);
  }
  const legacy = window.localStorage.getItem(USER_ROLE_STORAGE_KEY);
  if (legacy && DISPLAY_USER_ROLE_VALUES.includes(legacy as DisplayUserRole)) {
    const migrated = matchingSlugForDisplayShell(
      availableRoles,
      legacy as DisplayUserRole,
      primaryRole,
    );
    if (migrated) {
      const coerced = getEffectiveActiveRoleSlug(availableRoles, migrated, primaryRole);
      window.localStorage.setItem(key, coerced);
      window.localStorage.removeItem(USER_ROLE_STORAGE_KEY);
      return coerced;
    }
  }
  return getEffectiveActiveRoleSlug(availableRoles, undefined, primaryRole);
}

export function writeStoredActiveRoleSlug(practitionerId: string, slug: Role): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(activeRoleStorageKey(practitionerId), slug);
}

export function readStoredDisplayUserRole(): DisplayUserRole {
  if (typeof window === 'undefined') {
    return 'Unify Admin';
  }
  const saved = window.localStorage.getItem(USER_ROLE_STORAGE_KEY);
  if (saved && DISPLAY_USER_ROLE_VALUES.includes(saved as DisplayUserRole)) {
    return saved as DisplayUserRole;
  }
  return 'Unify Admin';
}

/**
 * Effective display shell: derived from the session active role slug when RBAC is ready.
 */
export function getEffectiveDisplayUserRole(
  rbac: { ready: boolean; role: Role; activeRole?: Role; availableRoles?: Role[] },
  storedFallback: DisplayUserRole,
): DisplayUserRole {
  if (!rbac.ready) return storedFallback;
  const slug = rbac.activeRole ?? rbac.role;
  return ROLE_SLUG_TO_DISPLAY_USER_ROLE[slug] ?? storedFallback;
}

/** Display shells the user can switch between (deduplicated). Prefer slug switcher. */
export function getAvailableDisplayUserRoles(
  rbac: { ready: boolean; role: Role; availableRoles?: Role[] },
): DisplayUserRole[] {
  if (!rbac.ready) return [...DISPLAY_USER_ROLE_VALUES];
  const available = rbac.availableRoles ?? [rbac.role];
  const displayRoles = available
    .map((r) => ROLE_SLUG_TO_DISPLAY_USER_ROLE[r])
    .filter((d): d is DisplayUserRole => d !== undefined);
  return [...new Set(displayRoles)];
}

export function getAvailableRoleSlugs(
  rbac: { ready: boolean; role: Role; availableRoles?: Role[] },
): Role[] {
  if (!rbac.ready) return [];
  return rbac.availableRoles ?? [rbac.role];
}

/** Prefer supervisor when both clinical roles are held so session UI includes cosign. */
export function coerceSwitchableRoleSlug(slug: Role, availableRoles: Role[]): Role {
  if (slug === 'provider' && availableRoles.includes('supervisor')) return 'supervisor';
  return slug;
}

export function useEffectiveDisplayUserRole(): DisplayUserRole {
  const rbac = usePermissions();
  return getEffectiveDisplayUserRole(rbac, readStoredDisplayUserRole());
}
